/** Material UI Packages. */
import { GridActionsCellItem } from '@mui/x-data-grid';

/** Material UI Packages. */
import { Typography, Link } from '@mui/material';

/** Material UI Icons. */
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import FileUploadIcon from '@mui/icons-material/FileUpload';

/** Internal Components. */
import {
    prealert_status,
    customs_status,
    customs_hold_status,
    proof_status,
    delivery_order_status,
    import_service_fee_status,
    arrival_notice_status,
    invoice_crdr_status,
} from 'shared/config/constantsData';
import {
    CustomSelectCell_string,
    CustomAutocompleteCell_string,
    CustomAutocompleteCell_TradePartner,
    FillDownHeader,
    MultiLinkCell,
    MultiLinkEditCell,
    DateTypingEditCell,
} from 'shared/ui/RenderCell';
import GoogleRenderCell from 'shared/ui/GoogleRenderCell';
import { customized_style } from 'shared/config/Styles';
import { createHeaderGroup, createMultiHeaderGroup } from 'shared/ui/Tooltip';
import { getClassName, getHeaderClass } from 'shared/lib/todoColorHandler';
import { parseDateToLocal } from 'shared/lib/dateFormatter';
import {
    createOptionPasteParser,
    createTradePartnerValueParser,
    tradePartnerValueFormatter,
    parsePastedDate,
    objectValueFormatter,
    createObjectValueParser,
} from 'shared/lib/datagridHelpers';
import { isAssistantManagerOrAbove } from 'shared/lib/roles';

export const hblCol = (
    userRole,
    presetData,
    tab,
    todo,
    unitOptions,
    handleUnitChange,
    tradePartnerOptions,
    handleTradePartnerChange,
    handleFormatMismatch,
) => {
    const preAlert = presetData?.pre_alert;
    const importService = presetData?.import_service_fee;

    const filtered_todo = todo?.filter((item) => item.filter_id === tab.id);

    let isHighlighted_prealert = filtered_todo?.length ? getClassName(filtered_todo, ['pre_alert'], 'header') : '';
    let isHighlighted_importservice = filtered_todo?.length
        ? getClassName(filtered_todo, ['import_service_fee'], 'header')
        : '';

    return [
        {
            field: 'id',
            headerName: 'ID',
            flex: 1,
            align: 'center',
            headerAlign: 'center',
            hide: true,
        },
        {
            field: 'il_trade_partner_id_shipper',
            headerName: 'Shipper',
            flex: 1,
            editable: true,
            align: 'center',
            headerAlign: 'center',
            // Copy/paste carries the whole partner object as JSON (same pattern as the bulk dialog
            // and location cells); a plain name or a location object is rejected before it can be saved.
            valueFormatter: objectValueFormatter,
            valueParser: createObjectValueParser({ key: 'il_trade_partner_id_shipper', label: 'Shipper' }, handleFormatMismatch),
            renderCell: (params) => {
                return (
                    <Typography sx={customized_style.itemDatagrid_Typography}>{params.value?.name || ''}</Typography>
                );
            },
            renderEditCell: (params) => {
                return (
                    <CustomAutocompleteCell_TradePartner
                        params={params}
                        options={tradePartnerOptions.il_trade_partner_id_shipper.options}
                        handleInputChange={handleTradePartnerChange}
                    />
                );
            },
        },
        {
            field: 'il_trade_partner_id_consignee',
            headerName: 'Consignee',
            flex: 1,
            editable: true,
            align: 'center',
            headerAlign: 'center',
            // Copy/paste carries the whole partner object as JSON (same pattern as the bulk dialog
            // and location cells); a plain name or a location object is rejected before it can be saved.
            valueFormatter: objectValueFormatter,
            valueParser: createObjectValueParser({ key: 'il_trade_partner_id_consignee', label: 'Consignee' }, handleFormatMismatch),
            renderCell: (params) => {
                return (
                    <Typography sx={customized_style.itemDatagrid_Typography}>{params.value?.name || ''}</Typography>
                );
            },
            renderEditCell: (params) => {
                return (
                    <CustomAutocompleteCell_TradePartner
                        params={params}
                        options={tradePartnerOptions.il_trade_partner_id_consignee.options}
                        handleInputChange={handleTradePartnerChange}
                    />
                );
            },
        },
        {
            field: 'hawb_link',
            headerName: 'Link',
            flex: 1,
            editable: true,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params) => {
                return (
                    <Link href={params.value ? `${params.value}` : '#'} target="_blank" rel="noopener">
                        {params.value ? 'View File' : ''}
                    </Link>
                );
            },
        },
        {
            field: 'reference',
            headerName: 'Reference',
            flex: 1.2,
            editable: true,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params) => (
                <Typography sx={customized_style.itemDatagrid_Typography}>{params.value}</Typography>
            ),
        },
        {
            field: 'import_service_fee',
            headerName: 'Status',
            flex: 1,
            editable: importService,
            align: 'center',
            headerAlign: 'center',
            type: 'singleSelect',
            headerClassName: getHeaderClass(importService, isHighlighted_importservice),
            renderCell: (params) => {
                return <span style={{ color: importService ? 'inherit' : '#C7C7C7' }}>{params.value}</span>;
            },
            renderEditCell: (params) => <CustomSelectCell_string params={params} options={import_service_fee_status} />,
        },
        {
            field: 'import_service_fee_link',
            headerName: 'Link',
            flex: 1,
            editable: importService,
            align: 'center',
            headerAlign: 'center',
            headerClassName: getHeaderClass(importService, isHighlighted_importservice),
            renderCell: (params) => {
                const linkStyle = !importService ? { color: '#C7C7C7' } : {};
                const handleClick = (event) => {
                    if (!importService) {
                        event.preventDefault();
                    }
                };
                return (
                    <Link
                        href={params.value ? `${params.value}` : '#'}
                        target="_blank"
                        rel="noopener"
                        style={linkStyle}
                        onClick={handleClick}
                    >
                        {params.value ? 'View File' : ''}
                    </Link>
                );
            },
        },
        {
            field: 'import_service_fee_date',
            headerName: 'Date',
            type: 'date',
            pastedValueParser: parsePastedDate,
            flex: 1,
            editable: importService,
            align: 'center',
            headerAlign: 'center',
            headerClassName: getHeaderClass(importService, isHighlighted_importservice),
            valueGetter: (params) => {
                return parseDateToLocal(params);
            },
            renderCell: (params) => {
                const date = parseDateToLocal(params.value);
                const formattedDate = date
                    ? date.toLocaleDateString('en-US', {
                          year: '2-digit',
                          month: '2-digit',
                          day: '2-digit',
                      })
                    : '';

                return (
                    <Typography
                        sx={{
                            ...customized_style.itemDatagrid_Typography,
                            color: importService ? 'inherit' : '#C7C7C7',
                        }}
                    >
                        {formattedDate}
                    </Typography>
                );
            },
            renderEditCell: (params) => <DateTypingEditCell params={params} />,
        },
        {
            field: 'pre_alert',
            headerName: 'Status',
            flex: 1,
            editable: preAlert,
            align: 'center',
            headerAlign: 'center',
            type: 'singleSelect',
            headerClassName: getHeaderClass(preAlert, isHighlighted_prealert),
            renderCell: (params) => {
                return <span style={{ color: preAlert ? 'inherit' : '#C7C7C7' }}>{params.value}</span>;
            },
            renderEditCell: (params) => <CustomSelectCell_string params={params} options={prealert_status} />,
        },
    ];
};

export const hblCol2 = (
    userRole,
    presetData,
    tab,
    todo,
    unitOptions,
    handleUnitChange,
    tradePartnerOptions,
    handleTradePartnerChange
) => {
    const arrivalNotice = presetData?.arrival_notice;
    const invoice = presetData?.invoice;
    const crdr = presetData?.crdr;

    const filtered_todo = todo?.filter((item) => item.filter_id === tab.id);
    let isHighlighted_arrivalnotice = filtered_todo?.length
        ? getClassName(filtered_todo, ['arrival_notice'], 'header')
        : '';
    let isHighlighted_invoice = filtered_todo?.length ? getClassName(filtered_todo, ['invoice'], 'header') : '';
    let isHighlighted_crdr = filtered_todo?.length ? getClassName(filtered_todo, ['crdr'], 'header') : '';

    return [
        {
            field: 'id',
            headerName: 'ID',
            flex: 1,
            align: 'center',
            headerAlign: 'center',
            hide: true,
        },
        {
            field: 'arrival_notice',
            headerName: 'Status',
            type: 'singleSelect',
            flex: 1,
            editable: arrivalNotice,
            align: 'center',
            headerAlign: 'center',
            headerClassName: getHeaderClass(arrivalNotice, isHighlighted_arrivalnotice),
            renderCell: (params) => {
                return <span style={{ color: arrivalNotice ? 'inherit' : '#C7C7C7' }}>{params.value}</span>;
            },
            renderEditCell: (params) => <CustomSelectCell_string params={params} options={arrival_notice_status} />,
        },
        {
            field: 'arrival_notice_link',
            headerName: 'Link',
            flex: 1,
            editable: arrivalNotice,
            align: 'center',
            headerAlign: 'center',
            headerClassName: getHeaderClass(arrivalNotice, isHighlighted_arrivalnotice),
            renderCell: (params) => {
                const linkStyle = !arrivalNotice ? { color: '#C7C7C7' } : {};
                const handleClick = (event) => {
                    if (!arrivalNotice) {
                        event.preventDefault();
                    }
                };
                return (
                    <Link
                        href={params.value ? `${params.value}` : '#'}
                        target="_blank"
                        rel="noopener"
                        style={linkStyle}
                        onClick={handleClick}
                    >
                        {params.value ? 'View File' : ''}
                    </Link>
                );
            },
        },
        {
            field: 'invoice',
            headerName: 'Status',
            type: 'singleSelect',
            flex: 1,
            editable: invoice,
            align: 'center',
            headerAlign: 'center',
            valueOptions: invoice_crdr_status,
            headerClassName: getHeaderClass(invoice, isHighlighted_invoice),
            renderCell: (params) => {
                return <span style={{ color: invoice ? 'inherit' : '#C7C7C7' }}>{params.value}</span>;
            },
            renderEditCell: (params) => {
                const allowedStatuses = ['Pending', 'Ready', 'Sent'];
                const hasOperationManagerRole =
                    isAssistantManagerOrAbove(userRole);
                return (
                    <CustomSelectCell_string
                        params={params}
                        options={invoice_crdr_status}
                        hasOperationManagerRole={hasOperationManagerRole}
                        allowedStatuses={allowedStatuses}
                    />
                );
            },
        },
        {
            field: 'invoice_link',
            headerName: 'Link',
            flex: 1,
            editable: invoice,
            align: 'center',
            headerAlign: 'center',
            headerClassName: getHeaderClass(invoice, isHighlighted_invoice),
            renderCell: (params) => <MultiLinkCell params={params} enabled={invoice} />,
            renderEditCell: (params) => <MultiLinkEditCell params={params} />,
        },
        {
            field: 'invoice_date',
            headerName: 'Date',
            type: 'date',
            pastedValueParser: parsePastedDate,
            flex: 1,
            editable: invoice,
            align: 'center',
            headerAlign: 'center',
            headerClassName: getHeaderClass(invoice, isHighlighted_invoice),
            valueGetter: (params) => {
                return parseDateToLocal(params);
            },
            renderCell: (params) => {
                const date = parseDateToLocal(params.value);
                const formattedDate = date
                    ? date.toLocaleDateString('en-US', {
                          year: '2-digit',
                          month: '2-digit',
                          day: '2-digit',
                      })
                    : '';

                return (
                    <Typography
                        sx={{
                            ...customized_style.itemDatagrid_Typography,
                            color: invoice ? 'inherit' : '#C7C7C7',
                        }}
                    >
                        {formattedDate}
                    </Typography>
                );
            },
            renderEditCell: (params) => <DateTypingEditCell params={params} />,
        },
        {
            field: 'crdr',
            headerName: 'Status',
            type: 'singleSelect',
            flex: 1,
            editable: crdr,
            align: 'center',
            headerAlign: 'center',
            valueOptions: invoice_crdr_status,
            headerClassName: getHeaderClass(crdr, isHighlighted_crdr),
            renderCell: (params) => {
                return <span style={{ color: crdr ? 'inherit' : '#C7C7C7' }}>{params.value}</span>;
            },
            renderEditCell: (params) => {
                const allowedStatuses = ['Pending', 'Ready', 'Sent'];
                const hasOperationManagerRole =
                    isAssistantManagerOrAbove(userRole);
                return (
                    <CustomSelectCell_string
                        params={params}
                        options={invoice_crdr_status}
                        hasOperationManagerRole={hasOperationManagerRole}
                        allowedStatuses={allowedStatuses}
                    />
                );
            },
        },
        {
            field: 'crdr_link',
            headerName: 'Link',
            flex: 1,
            editable: crdr,
            align: 'center',
            headerAlign: 'center',
            headerClassName: getHeaderClass(crdr, isHighlighted_crdr),
            renderCell: (params) => <MultiLinkCell params={params} enabled={crdr} />,
            renderEditCell: (params) => <MultiLinkEditCell params={params} />,
        },
        {
            field: 'crdr_date',
            headerName: 'Date',
            type: 'date',
            pastedValueParser: parsePastedDate,
            flex: 1,
            editable: crdr,
            align: 'center',
            headerAlign: 'center',
            headerClassName: getHeaderClass(crdr, isHighlighted_crdr),
            valueGetter: (params) => {
                return parseDateToLocal(params);
            },
            renderCell: (params) => {
                const date = parseDateToLocal(params.value);
                const formattedDate = date
                    ? date.toLocaleDateString('en-US', {
                          year: '2-digit',
                          month: '2-digit',
                          day: '2-digit',
                      })
                    : '';

                return (
                    <Typography
                        sx={{
                            ...customized_style.itemDatagrid_Typography,
                            color: crdr ? 'inherit' : '#C7C7C7',
                        }}
                    >
                        {formattedDate}
                    </Typography>
                );
            },
            renderEditCell: (params) => <DateTypingEditCell params={params} />,
        },
    ];
};

export const hblAliasCol = (
    handleDeleteClick,
    presetData,
    unitOptions,
    handleUnitChange,
    tradePartnerOptions,
    handleTradePartnerChange,
    matchedTodoContainer,
    handleLinkClick,
    mainRow,
    handleFillDown,
    onInvalidOption
) => {
    const customs = presetData?.customs;
    const deliveryOrder = presetData?.delivery_order;
    const proof = presetData?.proof;

    return [
        {
            field: 'id',
            headerName: 'ID',
            flex: 1,
            align: 'center',
            headerAlign: 'center',
            hide: true,
        },

        {
            field: 'alias',
            headerName: 'Alias',
            flex: 0.8,
            editable: true,
            align: 'center',
            headerAlign: 'center',
            renderHeader: () => <FillDownHeader label="Alias" field="alias" onFillDown={handleFillDown} />,
        },
        {
            field: 'il_opus_vessel_id',
            headerName: 'Flight#',
            flex: 0.8,
            editable: true,
            align: 'center',
            headerAlign: 'center',
            renderHeader: () => (
                <FillDownHeader label="Flight#" field="il_opus_vessel_id" onFillDown={handleFillDown} />
            ),
        },
        {
            field: 'quantity',
            headerName: 'Vol',
            flex: 0.5,
            editable: true,
            align: 'center',
            headerAlign: 'center',
            type: 'number',
            renderHeader: () => <FillDownHeader label="Vol" field="quantity" onFillDown={handleFillDown} />,
        },
        {
            field: 'il_opus_volume_unit_id',
            headerName: 'Unit',
            flex: 0.7,
            editable: true,
            align: 'center',
            headerAlign: 'center',
            renderHeader: () => (
                <FillDownHeader label="Unit" field="il_opus_volume_unit_id" onFillDown={handleFillDown} />
            ),
            renderEditCell: (params) => (
                <CustomAutocompleteCell_string
                    params={params}
                    options={unitOptions}
                    handleInputChange={(event, value) => handleUnitChange(event, value)}
                    shouldFocus={true}
                />
            ),
        },
        {
            field: 'hazmat',
            headerName: 'Hazmat',
            flex: 0.7,
            editable: true,
            align: 'center',
            headerAlign: 'center',
            type: 'boolean',
            renderHeader: () => <FillDownHeader label="Hazmat" field="hazmat" onFillDown={handleFillDown} />,
            valueGetter: (value) => value === true,
        },
        {
            field: 'weight',
            headerName: 'Weight',
            flex: 0.6,
            editable: true,
            align: 'center',
            headerAlign: 'center',
            type: 'number',
            renderHeader: () => <FillDownHeader label="Weight" field="weight" onFillDown={handleFillDown} />,
            renderCell: (params) => `${params.value || 0} kg`,
        },
        {
            field: 'customs',
            headerName: 'Customs',
            flex: 0.8,
            editable: customs,
            align: 'center',
            headerAlign: 'center',
            type: 'singleSelect',
            headerClassName: customs ? '' : 'disabled-header',
            renderHeader: () => <FillDownHeader label="Customs" field="customs" onFillDown={handleFillDown} />,
            pastedValueParser: createOptionPasteParser(customs_status, onInvalidOption),
            cellClassName: (params) => {
                const fitlered_todo = matchedTodoContainer.filter((item) => item.filter_id === params.row.id);
                return getClassName(fitlered_todo, ['customs', 'customs_hold'], 'header-cell');
            },
            renderCell: (params) => {
                return <span style={{ color: customs ? 'inherit' : '#C7C7C7' }}>{params.value}</span>;
            },
            renderEditCell: (params) => <CustomSelectCell_string params={params} options={customs_status} />,
        },
        {
            field: 'customs_hold',
            headerName: 'Hold/Release',
            flex: 1,
            editable: customs,
            align: 'center',
            headerAlign: 'center',
            type: 'singleSelect',
            headerClassName: customs ? '' : 'disabled-header',
            renderHeader: () => (
                <FillDownHeader label="Hold/Release" field="customs_hold" onFillDown={handleFillDown} />
            ),
            pastedValueParser: createOptionPasteParser(customs_hold_status, onInvalidOption),
            cellClassName: (params) => {
                const fitlered_todo = matchedTodoContainer.filter((item) => item.filter_id === params.row.id);
                return getClassName(fitlered_todo, ['customs', 'customs_hold'], 'header-cell');
            },
            renderCell: (params) => {
                return <span style={{ color: customs ? 'inherit' : '#C7C7C7' }}>{params.value}</span>;
            },
            renderEditCell: (params) => <CustomSelectCell_string params={params} options={customs_hold_status} />,
        },
        {
            field: 'customs_other',
            headerName: 'Others',
            flex: 1,
            editable: customs,
            align: 'center',
            headerAlign: 'center',
            headerClassName: customs ? '' : 'disabled-header',
            renderHeader: () => <FillDownHeader label="Others" field="customs_other" onFillDown={handleFillDown} />,
            cellClassName: (params) => {
                const fitlered_todo = matchedTodoContainer.filter((item) => item.filter_id === params.row.id);
                return getClassName(fitlered_todo, ['customs', 'customs_hold'], 'header-cell');
            },
        },
        {
            field: 'il_location_id_deliver_to',
            headerName: 'Delivery To',
            flex: 1.2,
            editable: deliveryOrder,
            align: 'center',
            headerAlign: 'center',
            headerClassName: deliveryOrder ? '' : 'disabled-header',
            renderHeader: () => (
                <FillDownHeader label="Delivery To" field="il_location_id_deliver_to" onFillDown={handleFillDown} />
            ),
            cellClassName: (params) => {
                const fitlered_todo = matchedTodoContainer.filter((item) => item.filter_id === params.row.id);
                return getClassName(fitlered_todo, 'delivery_order', 'header-cell');
            },
            renderCell: (params) => (
                <Typography
                    sx={{ ...customized_style.itemDatagrid_Typography, color: deliveryOrder ? 'inherit' : '#C7C7C7' }}
                >
                    {params.value}
                </Typography>
            ),
            renderEditCell: (params) => (
                <GoogleRenderCell
                    params={params}
                    shouldFocus={true}
                    reducer={'setAimContainerFormData'}
                    tableName={'il_air_import_hbl_alias'}
                    hasRowUpdate={false}
                    tableId={[mainRow.id, params.row.il_air_import_hbl_id]}
                />
            ),
        },
        {
            field: 'il_trade_partner_id',
            headerName: 'Trucker Name',
            flex: 1,
            editable: deliveryOrder,
            align: 'center',
            headerAlign: 'center',
            headerClassName: deliveryOrder ? '' : 'disabled-header',
            renderHeader: () => (
                <FillDownHeader label="Trucker Name" field="il_trade_partner_id" onFillDown={handleFillDown} />
            ),
            valueParser: createTradePartnerValueParser(
                'il_trade_partner_id',
                tradePartnerOptions?.il_trade_partner_id?.options
            ),
            valueFormatter: tradePartnerValueFormatter,
            cellClassName: (params) => {
                const fitlered_todo = matchedTodoContainer.filter((item) => item.filter_id === params.row.id);
                return getClassName(fitlered_todo, 'delivery_order', 'header-cell');
            },
            renderCell: (params) => {
                return (
                    <Typography
                        sx={{
                            ...customized_style.itemDatagrid_Typography,
                            color: deliveryOrder ? 'inherit' : '#C7C7C7',
                        }}
                    >
                        {params?.value?.name || ''}
                    </Typography>
                );
            },
            renderEditCell: (params) => {
                return (
                    <CustomAutocompleteCell_TradePartner
                        params={params}
                        options={tradePartnerOptions.il_trade_partner_id.options}
                        handleInputChange={handleTradePartnerChange}
                    />
                );
            },
        },
        {
            field: 'delivery_order',
            headerName: 'Status',
            flex: 0.7,
            editable: deliveryOrder,
            align: 'center',
            headerAlign: 'center',
            type: 'singleSelect',
            headerClassName: deliveryOrder ? '' : 'disabled-header',
            renderHeader: () => <FillDownHeader label="Status" field="delivery_order" onFillDown={handleFillDown} />,
            pastedValueParser: createOptionPasteParser(delivery_order_status, onInvalidOption),
            cellClassName: (params) => {
                const fitlered_todo = matchedTodoContainer.filter((item) => item.filter_id === params.row.id);
                return getClassName(fitlered_todo, 'delivery_order', 'header-cell');
            },
            renderCell: (params) => {
                return <span style={{ color: deliveryOrder ? 'inherit' : '#C7C7C7' }}>{params.value}</span>;
            },
            renderEditCell: (params) => <CustomSelectCell_string params={params} options={delivery_order_status} />,
        },
        {
            field: 'proof',
            headerName: 'Status',
            flex: 0.7,
            editable: proof,
            align: 'center',
            headerAlign: 'center',
            type: 'singleSelect',
            headerClassName: proof ? '' : 'disabled-header',
            renderHeader: () => <FillDownHeader label="Status" field="proof" onFillDown={handleFillDown} />,
            pastedValueParser: createOptionPasteParser(proof_status, onInvalidOption),
            cellClassName: (params) => {
                const fitlered_todo = matchedTodoContainer.filter((item) => item.filter_id === params.row.id);
                return getClassName(fitlered_todo, 'proof', 'header-cell');
            },
            renderCell: (params) => {
                return <span style={{ color: proof ? 'inherit' : '#C7C7C7' }}>{params.value}</span>;
            },
            renderEditCell: (params) => <CustomSelectCell_string params={params} options={proof_status} />,
        },
        {
            field: 'proof_date',
            headerName: 'Date',
            type: 'date',
            pastedValueParser: parsePastedDate,
            flex: 0.6,
            editable: proof,
            align: 'center',
            headerAlign: 'center',
            headerClassName: proof ? '' : 'disabled-header',
            renderHeader: () => <FillDownHeader label="Date" field="proof_date" onFillDown={handleFillDown} />,
            cellClassName: (params) => {
                const fitlered_todo = matchedTodoContainer.filter((item) => item.filter_id === params.row.id);
                return getClassName(fitlered_todo, 'proof', 'header-cell');
            },
            valueGetter: (params) => {
                return parseDateToLocal(params);
            },
            renderCell: (params) => {
                const date = parseDateToLocal(params.value);
                const formattedDate = date
                    ? date.toLocaleDateString('en-US', {
                          year: '2-digit',
                          month: '2-digit',
                          day: '2-digit',
                      })
                    : '';

                return (
                    <Typography
                        sx={{
                            ...customized_style.itemDatagrid_Typography,
                            color: proof ? 'inherit' : '#C7C7C7',
                        }}
                    >
                        {formattedDate}
                    </Typography>
                );
            },
            renderEditCell: (params) => <DateTypingEditCell params={params} />,
        },
        {
            field: 'actions',
            headerName: '',
            flex: 0.5,
            editable: true,
            align: 'center',
            headerAlign: 'center',
            type: 'actions',
            getActions: (params) => {
                const needsCustoms = presetData?.customs && !params.row.customs_link;
                const needsDeliveryOrder = presetData?.delivery_order && !params.row.delivery_order_link;
                const needsProof = presetData?.proof && !params.row.proof_link;
                const shouldInput = needsCustoms || needsDeliveryOrder || needsProof;
                return [
                    <GridActionsCellItem
                        icon={<FileUploadIcon sx={{ color: shouldInput ? '#f48704' : 'rgba(0, 0, 0, 0.54)' }} />}
                        label="Link"
                        onClick={() => handleLinkClick(params.row)}
                        color="inherit"
                    />,
                    <GridActionsCellItem
                        icon={<DeleteIcon sx={{ color: 'rgba(0, 0, 0, 0.54)' }} />}
                        label="Delete"
                        onClick={() => handleDeleteClick(params.id)}
                        color="inherit"
                    />,
                ];
            },
        },
    ];
};

export const linkCols = (presetData, matchedTodoContainer) => {
    const customs = presetData?.customs;
    const deliveryOrder = presetData?.delivery_order;
    const proof = presetData?.proof;

    return [
        {
            field: 'id',
            headerName: 'ID',
            flex: 1,
            align: 'center',
            headerAlign: 'center',
            sortable: false,
            hideable: false,
            hide: true,
            pinnable: false,
        },
        {
            field: 'customs_link',
            headerName: 'Customs Link',
            flex: 1,
            editable: customs,
            align: 'center',
            headerAlign: 'center',
            headerClassName: customs ? '' : 'disabled-header',
            cellClassName: (params) => {
                const fitlered_todo = matchedTodoContainer.filter((item) => item.filter_id === params.row.id);
                return getClassName(fitlered_todo, 'customs', 'header-cell');
            },
            renderCell: (params) => {
                const linkStyle = !customs ? { color: '#C7C7C7' } : {};
                const handleClick = (event) => {
                    if (!customs) {
                        event.preventDefault();
                    }
                };
                return (
                    <Link
                        href={params.value ? `${params.value}` : '#'}
                        target="_blank"
                        rel="noopener"
                        style={linkStyle}
                        onClick={handleClick}
                    >
                        {params.value ? 'View File' : ''}
                    </Link>
                );
            },
        },
        {
            field: 'delivery_order_link',
            headerName: 'Delivery Order Link',
            flex: 1,
            editable: deliveryOrder,
            align: 'center',
            headerAlign: 'center',
            headerClassName: deliveryOrder ? '' : 'disabled-header',
            cellClassName: (params) => {
                const fitlered_todo = matchedTodoContainer.filter((item) => item.filter_id === params.row.id);
                return getClassName(fitlered_todo, 'delivery_order', 'header-cell');
            },
            renderCell: (params) => {
                const linkStyle = !deliveryOrder ? { color: '#C7C7C7' } : {};
                const handleClick = (event) => {
                    if (!deliveryOrder) {
                        event.preventDefault();
                    }
                };
                return (
                    <Link
                        href={params.value ? `${params.value}` : '#'}
                        target="_blank"
                        rel="noopener"
                        style={linkStyle}
                        onClick={handleClick}
                    >
                        {params.value ? 'View File' : ''}
                    </Link>
                );
            },
        },
        {
            field: 'proof_link',
            headerName: 'Proof Link',
            flex: 1,
            editable: proof,
            align: 'center',
            headerAlign: 'center',
            headerClassName: proof ? '' : 'disabled-header',
            cellClassName: (params) => {
                const fitlered_todo = matchedTodoContainer.filter((item) => item.filter_id === params.row.id);
                return getClassName(fitlered_todo, 'proof', 'header-cell');
            },
            renderCell: (params) => {
                const linkStyle = !proof ? { color: '#C7C7C7' } : {};
                const handleClick = (event) => {
                    if (!proof) {
                        event.preventDefault();
                    }
                };
                return (
                    <Link
                        href={params.value ? `${params.value}` : '#'}
                        target="_blank"
                        rel="noopener"
                        style={linkStyle}
                        onClick={handleClick}
                    >
                        {params.value ? 'View File' : ''}
                    </Link>
                );
            },
        },
    ];
};

export const hblGroupingModel = (presetData, tabValue, historyData, tab) => {
    const preAlert = presetData?.pre_alert;
    const importService = presetData?.import_service_fee;
    return [
        {
            groupId: 'HAWB',
            headerAlign: 'center',
            children: [
                { field: 'il_trade_partner_id_shipper' },
                { field: 'il_trade_partner_id_consignee' },
                { field: 'hawb_link' },
                { field: 'reference' },
            ],
            renderHeaderGroup: (params) =>
                createHeaderGroup(params, historyData, tabValue, tab, [
                    'il_trade_partner_id_shipper',
                    'il_trade_partner_id_consignee',
                    'hawb_link',
                    'reference',
                ]),
        },
        {
            groupId: 'Import Service Fee',
            headerAlign: 'center',
            children: [
                { field: 'import_service_fee' },
                { field: 'import_service_fee_link' },
                { field: 'import_service_fee_date' },
            ],
            headerClassName: importService ? '' : 'disabled-header',
            renderHeaderGroup: (params) =>
                createHeaderGroup(params, historyData, tabValue, tab, [
                    'import_service_fee',
                    'import_service_fee_date',
                    'import_service_fee_link',
                ]),
        },
        {
            groupId: 'Pre Alert',
            headerAlign: 'center',
            children: [{ field: 'pre_alert' }],
            headerClassName: preAlert ? '' : 'disabled-header',
            renderHeaderGroup: (params) => createHeaderGroup(params, historyData, tabValue, tab, ['pre_alert']),
        },
    ];
};

export const hblGroupingModel2 = (presetData, tabValue, historyData, tab) => {
    const arrival_notice = presetData?.arrival_notice;
    const invoice = presetData?.invoice;
    const crdr = presetData?.crdr;

    return [
        {
            groupId: 'Arrival Notice',
            headerAlign: 'center',
            children: [{ field: 'arrival_notice' }, { field: 'arrival_notice_link' }],
            headerClassName: arrival_notice ? '' : 'disabled-header',
            renderHeaderGroup: (params) =>
                createHeaderGroup(params, historyData, tabValue, tab, ['arrival_notice', 'arrival_notice_link']),
        },
        {
            groupId: 'Invoice',
            headerAlign: 'center',
            children: [{ field: 'invoice' }, { field: 'invoice_link' }, { field: 'invoice_date' }],
            headerClassName: invoice ? '' : 'disabled-header',
            renderHeaderGroup: (params) =>
                createHeaderGroup(params, historyData, tabValue, tab, ['invoice', 'invoice_link', 'invoice_date']),
        },
        {
            groupId: 'Credit/Debit Note',
            headerAlign: 'center',
            children: [{ field: 'crdr' }, { field: 'crdr_link' }, { field: 'crdr_date' }],
            headerClassName: crdr ? '' : 'disabled-header',
            renderHeaderGroup: (params) =>
                createHeaderGroup(params, historyData, tabValue, tab, ['crdr', 'crdr_link', 'crdr_date']),
        },
    ];
};

export const hblAliasGroupingModel = (presetData, tabValue, historyData, tab) => {
    const customs = presetData?.customs;
    const deliveryOrder = presetData?.delivery_order;
    const proof = presetData?.proof;

    return [
        {
            groupId: 'Air Cargo',
            headerAlign: 'center',
            children: [
                { field: 'alias' },
                { field: 'il_opus_vessel_id' },
                { field: 'quantity' },
                { field: 'il_opus_volume_unit_id' },
                { field: 'hazmat' },
                { field: 'weight' },
            ],
            renderHeaderGroup: (params) =>
                createMultiHeaderGroup(params, historyData, tabValue, tab, [
                    'alias',
                    'il_opus_vessel_id',
                    'quantity',
                    'il_opus_volume_unit_id',
                    'hazmat',
                    'weight',
                ]),
        },
        {
            groupId: 'Customs',
            headerAlign: 'center',
            children: [
                { field: 'customs' },
                { field: 'customs_hold' },
                { field: 'customs_link' },
                { field: 'customs_other' },
            ],
            headerClassName: customs ? '' : 'disabled-header',
            renderHeaderGroup: (params) =>
                createMultiHeaderGroup(params, historyData, tabValue, tab, [
                    'customs',
                    'customs_hold',
                    'customs_other',
                    'customs_link',
                ]),
        },

        {
            groupId: 'Delivery Order',
            headerAlign: 'center',
            children: [
                { field: 'il_location_id_deliver_to' },
                { field: 'il_trade_partner_id' },
                { field: 'delivery_order' },
                { field: 'delivery_order_link' },
            ],
            headerClassName: deliveryOrder ? '' : 'disabled-header',
            renderHeaderGroup: (params) =>
                createMultiHeaderGroup(params, historyData, tabValue, tab, [
                    'delivery_order',
                    'delivery_order_link',
                    'il_location_id_deliver_to',
                    'il_trade_partner_id',
                ]),
        },
        {
            groupId: 'Proof',
            headerAlign: 'center',
            children: [{ field: 'proof' }, { field: 'proof_link' }, { field: 'proof_date' }],
            headerClassName: proof ? '' : 'disabled-header',
            renderHeaderGroup: (params) =>
                createMultiHeaderGroup(params, historyData, tabValue, tab, ['proof', 'proof_date', 'proof_link']),
        },
    ];
};
